
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sparkles,
  BookOpen,
  Cloud,
  Star,
  Heart,
  Smile,
  Lightbulb,
  ArrowUpRight,
  ShieldCheck,
  LogOut,
  Phone,
} from "lucide-react";

const ICONS = { Sparkles, BookOpen, Cloud, Star, Heart, Smile, Lightbulb };

export default function Community() {
  const { user, logout } = useAuth();
  const [channels, setChannels] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    api.get("/channels")
      .then(({ data }) => { if (live) setChannels(data); })
      .catch((e) => { if (live) setErr(formatApiError(e)); });
    return () => { live = false; };
  }, []);

  return (
    <div data-testid="page-community" className="py-16 lg:py-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
          <span className="w-6 h-px bg-terracotta" />
          The Community
        </span>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <h1 className="font-serif text-charcoal dark:text-bone text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            A quiet room for people who <em className="text-terracotta">get it</em>.
          </h1>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-charcoal-light dark:text-bone/65">
              Hello, <span className="text-charcoal dark:text-bone font-medium">{user?.first_name}</span>
            </span>
            <button
              type="button"
              onClick={logout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-bone-200 dark:border-charcoal-light/30 text-charcoal-light dark:text-bone/70 text-xs hover:border-terracotta hover:text-terracotta transition"
            >
              <LogOut size={14} strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>

        <p className="mt-6 text-lg text-charcoal-light dark:text-bone/70 leading-relaxed max-w-2xl">
          Pick a room. Share what feels safe to share. Be patient with yourself and with everyone here.
        </p>

        {/* Crisis bar */}
        <div
          data-testid="crisis-bar"
          className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 rounded-3xl border border-terracotta/30 bg-terracotta/10 dark:bg-terracotta/15 p-5"
        >
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-terracotta/20 text-terracotta shrink-0">
            <Phone size={18} strokeWidth={1.5} />
          </span>
          <p className="text-sm text-charcoal dark:text-bone leading-relaxed flex-1">
            <strong className="font-semibold">In crisis?</strong> If you’re in immediate danger call{" "}
            <a href="tel:000" className="underline underline-offset-4 decoration-terracotta">000</a>.
            For 24-hour support, Lifeline:{" "}
            <a href="tel:131114" className="underline underline-offset-4 decoration-terracotta">13 11 14</a>.
          </p>
          <Link
            to="/resources"
            data-testid="crisis-resources-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-terracotta hover:underline shrink-0"
          >
            All resources
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        {/* Admin link */}
        {user?.role === "admin" && (
          <Link
            to="/admin"
            data-testid="admin-link"
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-moss hover:text-terracotta transition"
          >
            <ShieldCheck size={14} strokeWidth={1.5} />
            Open admin dashboard
          </Link>
        )}

        {err && <p className="mt-6 text-sm text-terracotta">{err}</p>}

        {/* Channels */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="channel-list">
          {channels.map((c) => {
            const Icon = ICONS[c.icon] || Sparkles;
            return (
              <Link
                key={c.id}
                to={`/community/${c.slug}`}
                data-testid={`channel-${c.slug}`}
                className="group rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone dark:bg-charcoal-light/10 p-7 lg:p-8 hover-lift hover:border-terracotta/40 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta transition-all duration-500 group-hover:bg-terracotta group-hover:text-bone">
                      <Icon size={20} strokeWidth={1.5} />
                    </span>
                    <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight">
                      {c.name}
                    </h3>
                  </div>
                  <ArrowUpRight size={18} strokeWidth={1.5} className="text-charcoal-light dark:text-bone/55 group-hover:text-terracotta transition" />
                </div>
                <p className="mt-4 text-sm text-charcoal-light dark:text-bone/65 leading-relaxed">
                  {c.description}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Etiquette */}
        <div className="mt-16 rounded-3xl border border-bone-200 dark:border-charcoal-light/25 bg-bone-100/60 dark:bg-charcoal-light/10 p-8 lg:p-10" data-testid="etiquette">
          <h3 className="font-serif text-2xl text-charcoal dark:text-bone tracking-tight mb-3">
            A few quiet ground rules.
          </h3>
          <ul className="space-y-2.5 text-sm text-charcoal-light dark:text-bone/70 leading-relaxed">
            <li>• Believe people. FND is real.</li>
            <li>• No medical advice. Speak from your own experience.</li>
            <li>• If something feels off, use the report button — an admin will check.</li>
            <li>• Take breaks. The room will still be here.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
